<template>
  <div class="test-preview-container page-container bg-light-gradient">
    <div class="container-padded py-3">
      <!-- Header-Bereich -->
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h1 class="mb-0 animate-fade-in display-heading">
          <span class="icon icon-primary icon-circle me-2"><i class="fas fa-eye"></i></span> Testvorschau
        </h1>
        <div class="d-flex flex-wrap gap-2">
          <router-link :to="`/tests/${testId}/edit`" class="btn btn-primary btn-sm btn-with-icon with-hover-effect animate-fade-in">
            <span class="icon me-1"><i class="fas fa-edit"></i></span> Bearbeiten
          </router-link>
          <router-link to="/tests" class="btn btn-outline-secondary btn-sm btn-with-icon with-hover-effect animate-fade-in">
            <span class="icon me-1"><i class="fas fa-arrow-left"></i></span> Zurück
          </router-link>
        </div>
      </div>
      
      <!-- Lade-Animation -->
      <div v-if="loading" class="text-center py-4 content-loading">
        <div class="loader loader-md mb-2">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Wird geladen...</span>
          </div>
        </div>
        <p class="text-muted">Test wird geladen...</p>
      </div>
      
      <!-- Test-Zusammenfassung (vor dem Start) -->
      <template v-else-if="!previewMode && !showResults">
        <div class="row">
          <div class="col-12">
            <Card animateIn="fade-in-up" class="card-compact">
              <div class="d-flex align-items-center mb-3 test-header">
                <CompanyLogo :company="company" width="120px" class="me-2" />
                <div>
                  <h2 class="test-title mb-0">{{ test.name }}</h2>
                  <div class="test-meta">
                    <span class="badge me-2" :class="test.active ? 'bg-success' : 'bg-secondary'">
                      {{ test.active ? 'Aktiv' : 'Inaktiv' }}
                    </span>
                    <span class="meta-stat">
                      <span class="icon icon-muted me-1"><i class="fas fa-question-circle"></i></span> 
                      {{ questions.length }} Fragen
                    </span>
                  </div>
                </div>
              </div>
              
              <div class="alert alert-info alert-with-icon alert-compact">
                <span class="alert-icon"><i class="fas fa-info-circle"></i></span>
                <div>
                  <h5 class="alert-heading mb-1">Vorschaumodus</h5>
                  <p class="mb-0 small">Dies ist eine Vorschau des Tests. Sie können alle Fragen sehen und den Test durchgehen, aber keine Ergebnisse werden gespeichert.</p>
                </div>
              </div>
              
              <div v-if="test.instructions" class="instructions-panel mb-3">
                <h5 class="section-title mb-2">Anweisungen</h5>
                <div class="instructions-content p-2 bg-light rounded">
                  <p class="mb-0">{{ test.instructions }}</p>
                </div>
              </div>
              
              <button class="btn btn-primary btn-with-icon with-hover-effect" @click="startTest">
                <span class="icon me-1"><i class="fas fa-play"></i></span> Testvorschau starten
              </button>
            </Card>
          </div>
        </div>
      </template>
      
      <!-- Test-Durchführung -->
      <template v-else-if="previewMode">
        <Card animateIn="fade-in-up" class="card-compact">
          <!-- Testinfo und Modi-Umschaltung -->
          <div class="mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h2 class="mb-0 fs-4">{{ test.name }}</h2>
            <div class="d-flex align-items-center gap-2">
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" id="showAllQuestions" v-model="showAllQuestions">
                <label class="form-check-label small" for="showAllQuestions">Alle Fragen</label>
              </div>
              <button class="btn btn-outline-danger btn-sm btn-with-icon with-hover-effect" @click="exitPreview">
                <span class="icon me-1"><i class="fas fa-times"></i></span> Beenden
              </button>
            </div>
          </div>
          
          <!-- Progress-Bar (nur im Einzelfragen-Modus) -->
          <div v-if="!showAllQuestions" class="progress progress-enhanced mb-2">
            <div 
              class="progress-bar bg-primary" 
              role="progressbar" 
              :style="{ width: `${calculateProgressPercentage()}%` }"
              :aria-valuenow="calculateProgressPercentage()"
              aria-valuemin="0" 
              aria-valuemax="100"
            ></div>
          </div>
          
          <!-- Fragen-Navigation mit Legende -->
          <QuestionNavigationMenu 
            :questions="questions"
            :answers="answers"
            :currentIndex="currentQuestionIndex"
            :disableNavigation="showAllQuestions"
            @navigate="goToQuestion"
          />
          
          <!-- Einzelfragen-Ansicht -->
          <div v-if="!showAllQuestions && currentQuestion">
            <QuestionDisplay 
              :question="currentQuestion" 
              :index="currentQuestionIndex" 
              :totalQuestions="questions.length"
              :answer="answers[currentQuestionIndex]"
              @answer-changed="handleAnswerChange"
            />
            
            <!-- Navigation Controls -->
            <div class="nav-controls d-flex justify-content-between mt-3">
              <button 
                class="btn btn-outline-secondary btn-sm btn-with-icon with-hover-effect" 
                @click="prevQuestion"
                :disabled="currentQuestionIndex === 0"
              >
                <span class="icon me-1"><i class="fas fa-arrow-left"></i></span> Zurück
              </button>
              
              <button 
                v-if="currentQuestionIndex < questions.length - 1" 
                class="btn btn-primary btn-sm btn-with-icon with-hover-effect" 
                @click="nextQuestion"
              >
                <span>Weiter</span> <span class="icon ms-1"><i class="fas fa-arrow-right"></i></span>
              </button>
              
              <button 
                v-else 
                class="btn btn-success btn-sm btn-with-icon with-hover-effect"
                @click="finishTest"
              >
                <span>Abschließen</span> <span class="icon ms-1"><i class="fas fa-check"></i></span>
              </button>
            </div>
          </div>
          
          <!-- Alle-Fragen-Ansicht -->
          <div v-if="showAllQuestions" class="all-questions-view">
            <div 
              v-for="(question, qIndex) in questions" 
              :key="`all_q_${qIndex}`"
              class="question-card p-3 mb-2 border rounded bg-white"
            >
              <div class="question-header d-flex justify-content-between align-items-center mb-2">
                <span class="question-badge bg-primary">{{ qIndex + 1 }}</span>
                <button 
                  class="btn btn-sm btn-outline-primary btn-with-icon with-hover-effect"
                  @click="goToQuestion(qIndex)"
                >
                  <span class="icon me-1"><i class="fas fa-eye"></i></span> Beantworten
                </button>
              </div>
              
              <div class="question-body">
                <!-- Bild/Medien wenn vorhanden -->
                <div v-if="question.image_url" class="mb-2">
                  <img :src="question.image_url" :alt="`Bild für Frage ${qIndex + 1}`" class="img-fluid rounded">
                </div>
                
                <div class="question-text" v-html="question.description"></div>
              </div>
              
              <div v-if="question.type === 'multiple_choice'" class="question-options mt-2">
                <div 
                  v-for="(option, optIndex) in getQuestionOptions(question)" 
                  :key="`all_${qIndex}_${optIndex}`"
                  class="option-item p-2 border rounded mb-1"
                >
                  <div class="d-flex align-items-center">
                    <span class="option-number me-2">{{ optIndex + 1 }}.</span>
                    <span>{{ option }}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="d-flex justify-content-between mt-3">
              <button class="btn btn-outline-secondary btn-sm btn-with-icon with-hover-effect" @click="exitPreview">
                <span class="icon me-1"><i class="fas fa-times"></i></span> Beenden
              </button>
              <button class="btn btn-success btn-sm btn-with-icon with-hover-effect" @click="finishTest">
                <span class="icon me-1"><i class="fas fa-check"></i></span> Abschließen
              </button>
            </div>
          </div>
        </Card>
      </template>
      
      <!-- Ergebnis-Ansicht -->
      <template v-else-if="showResults">
        <TestResults 
          :questions="questions" 
          :answers="answers" 
          :score="calculateScore().score"
          :max-score="calculateScore().maxScore"
          :percentage="calculateScore().percentage"
          :showDetails="true"
        >
          <template #actions>
            <button class="btn btn-outline-secondary btn-sm btn-with-icon with-hover-effect" @click="exitPreview">
              <span class="icon me-1"><i class="fas fa-arrow-left"></i></span> Zurück
            </button>
            <button class="btn btn-primary btn-sm btn-with-icon with-hover-effect" @click="restartTest">
              <span class="icon me-1"><i class="fas fa-redo"></i></span> Neu starten
            </button>
          </template>
        </TestResults>
      </template>
    </div>
  </div>
</template>

<script>
import Card from '@/components/ui/Card.vue';
import CompanyLogo from '@/components/ui/CompanyLogo.vue';
import QuestionDisplay from '@/views/tests/components/QuestionDisplay.vue';
import TestResults from '@/views/tests/components/TestResults.vue';
import QuestionNavigationMenu from '@/views/tests/components/QuestionNavigationMenu.vue';
import { useTestsStore } from '@/stores/tests';

export default {
  name: 'TestPreview',
  components: {
    Card,
    CompanyLogo,
    QuestionDisplay,
    TestResults,
    QuestionNavigationMenu
  },
  setup() {
    const testsStore = useTestsStore();
    
    return {
      testsStore
    };
  },
  data() {
    return {
      loading: true,
      test: {},
      questions: [],
      previewMode: false,
      showResults: false,
      showAllQuestions: false,
      currentQuestionIndex: 0,
      answers: [], // Speichert die Antworten für jede Frage
      company: null
    };
  },
  computed: {
    testId() {
      return this.$route.params.id;
    },
    currentQuestion() {
      if (!this.questions.length || this.currentQuestionIndex >= this.questions.length) {
        return null;
      }
      return this.questions[this.currentQuestionIndex];
    }
  },
  methods: {
    async loadTest() {
      this.loading = true;
      
      try {
        // Test-Daten laden
        console.log('Lade Test:', this.testId);
        await this.testsStore.fetchTest(this.testId);
        
        // Test-Daten aus dem Store übernehmen
        const currentTest = this.testsStore.currentTest;
        if (!currentTest) {
          throw new Error('Test konnte nicht geladen werden');
        }
        
        this.test = { ...currentTest };
        this.company = this.test.company || null;
        
        // Fragen laden
        console.log('Lade Fragen für Test:', this.testId);
        await this.testsStore.fetchQuestions(this.testId);
        
        // Fragen aus dem Store übernehmen
        this.questions = [...this.testsStore.questions];
        
        // Antworten initialisieren
        this.initializeAnswers();
        
        console.log('Test und Fragen erfolgreich geladen');
      } catch (error) {
        console.error('Fehler beim Laden der Testdaten:', error);
        this.error = 'Fehler beim Laden der Testdaten. Bitte versuchen Sie es später erneut.';
      } finally {
        this.loading = false;
      }
    },
    startTest() {
      this.previewMode = true;
      this.showResults = false;
      this.currentQuestionIndex = 0;
      this.answers = new Array(this.questions.length).fill(null);
    },
    exitPreview() {
      this.previewMode = false;
      this.showResults = false;
    },
    restartTest() {
      this.previewMode = true;
      this.showResults = false;
      this.currentQuestionIndex = 0;
      this.answers = new Array(this.questions.length).fill(null);
    },
    goToQuestion(index) {
      if (index >= 0 && index < this.questions.length) {
        // Zur ausgewählten Frage wechseln
        this.currentQuestionIndex = index;
        
        // Wenn wir auf "Alle Fragen anzeigen" waren, wechseln wir zum Einzelfragenmodus
        this.showAllQuestions = false;
      }
    },
    getQuestionOptions(question) {
      // Verbesserte Methode zur Extraktion von Antwortoptionen
      const options = [];
      
      if (!question) return options;
      
      // Für beide mögliche Feldnamensformate prüfen
      for (let i = 1; i <= 6; i++) {
        const option = question[`answer_${i}`];
        
        // Nur nicht-leere Optionen hinzufügen
        if (option && option.trim() !== '') {
          options.push(option);
        }
      }
      
      return options;
    },
    handleAnswerChange(answer) {
      // Aktuelle Antwort speichern
      this.answers[this.currentQuestionIndex] = answer;
    },
    nextQuestion() {
      // Zur nächsten Frage wechseln
      if (this.currentQuestionIndex < this.questions.length - 1) {
        this.currentQuestionIndex++;
      }
    },
    prevQuestion() {
      // Zur vorherigen Frage wechseln
      if (this.currentQuestionIndex > 0) {
        this.currentQuestionIndex--;
      }
    },
    finishTest() {
      // Ergebnisse anzeigen
      this.previewMode = false;
      this.showResults = true;
    },
    isCorrectOption(question, optionIndex) {
      // Prüft ob eine Option korrekt ist, berücksichtigt beide mögliche Feldnamen
      if (question.correct_option) {
        // Wenn es ein konkretes correct_option-Feld gibt, ist das die richtige Antwort
        return question.correct_option === optionIndex;
      } else if (question[`correct_${optionIndex}`]) {
        // Alternative: Feld "correct_1", "correct_2" usw.
        return !!question[`correct_${optionIndex}`];
      } else if (question[`is_answer_${optionIndex}_correct`]) {
        // Alternative: Feld "is_answer_1_correct", "is_answer_2_correct" usw.
        return !!question[`is_answer_${optionIndex}_correct`];
      }
      return false;
    },
    calculateScore() {
      let score = 0;
      let maxScore = 0;
      
      this.questions.forEach((question, index) => {
        // Jede Frage hat standardmäßig 1 Punkt
        const questionPoints = question.points || 1;
        maxScore += questionPoints;
        
        if (this.isCorrectAnswer(question, index)) {
          score += questionPoints;
        }
      });
      
      return {
        score,
        maxScore,
        percentage: maxScore > 0 ? Math.round((score / maxScore) * 100) : 0
      };
    },
    isCorrectAnswer(question, index) {
      const answer = this.answers[index];
      
      // Keine Antwort = immer falsch
      if (!answer) return false;
      
      // Bei Multiple-Choice-Fragen
      if (question.type === 'multiple_choice') {
        // Fall 1: Wenn es eine korrekte Antwortoption gibt (radio buttons)
        if (question.correct_option) {
          return parseInt(answer) === parseInt(question.correct_option);
        }
        
        // Fall 2: Multiple-Choice mit mehreren richtigen Antwortmöglichkeiten (checkboxes)
        let isAnswerCorrect = true;
        const answersArray = Array.isArray(answer) ? answer : [answer];
        
        // Gehe alle möglichen Optionen durch
        const optionsCount = this.getQuestionOptions(question).length;
        for (let i = 1; i <= optionsCount; i++) {
          const isOptionCorrect = this.isCorrectOption(question, i);
          const isOptionSelected = answersArray.includes(i) || answersArray.includes(`${i}`);
          
          // Eine falsche Option wurde ausgewählt oder eine richtige Option nicht ausgewählt
          if ((isOptionCorrect && !isOptionSelected) || (!isOptionCorrect && isOptionSelected)) {
            isAnswerCorrect = false;
            break;
          }
        }
        
        return isAnswerCorrect;
      } else if (question.type === 'text' || question.type === 'open') {
        // Für Textfragen im Preview-Modus reicht es, wenn etwas eingegeben wurde
        // In einem echten Test müsste hier eine andere Logik für die Bewertung stehen
        return answer.trim().length > 0;
      }
      
      return false;
    },
    calculateProgressPercentage() {
      if (this.questions.length <= 1) return 0; // Vermeide Division durch Null
      return ((this.currentQuestionIndex) / (this.questions.length - 1)) * 100;
    },
    initializeAnswers() {
      this.answers = new Array(this.questions.length).fill(null);
    }
  },
  created() {
    this.loadTest();
  }
};
</script>

<style lang="scss" scoped>
.card-compact {
  padding: 1rem;
  border-radius: 12px;
}

.alert-compact {
  padding: 0.75rem 1rem;
  
  .alert-heading {
    font-size: 1rem;
  }
}

.option-item {
  border-radius: 10px;
  border-width: 2px;
  
  .option-number {
    font-weight: 600;
    font-size: 0.8rem;
    min-width: 16px;
    color: var(--primary-color, #3b82f6);
  }
}

.all-questions-view {
  .question-card {
    border-radius: 12px;
    border-width: 2px;
    transition: all 0.2s ease;
    
    &:hover {
      box-shadow: 0 3px 10px rgba(0, 0, 0, 0.08);
    }
    
    .question-text {
      font-size: 0.95rem;
      
      p:last-child {
        margin-bottom: 0;
      }
    }
  }
}

.progress-enhanced {
  height: 6px;
  overflow: hidden;
  background-color: var(--light-color, #f3f4f6);
  border-radius: 3px;
  
  .progress-bar {
    transition: width 0.3s ease;
  }
}

.btn-with-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &.btn-sm {
    padding: 0.35rem 0.7rem;
    font-size: 0.85rem;
  }
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.08);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  .icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
}

.question-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.3rem 0.6rem;
  border-radius: 8px;
}

.dark-mode {
  .card-compact {
    background-color: var(--dark-card-bg, #1f2937);
  }
  
  .question-card {
    background-color: var(--dark-card-bg, #1f2937) !important;
    border-color: var(--dark-border-color, #374151) !important;
  }
  
  .option-item {
    background-color: var(--dark-bg-secondary, #1f2937);
    border-color: var(--dark-border-color, #374151);
  }
  
  .progress-enhanced {
    background-color: var(--dark-bg-secondary, #1f2937);
  }
}
</style> 